import { type ApiClient, useApiQuery, useTeam } from '@vrplatform/data';
import { type Maybe, day, utc } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import type { recurringFeeType_enum } from '@vrplatform/graphql';
import {
  getListingAddress,
  getListingName,
  whereListingPeriod,
} from '@vrplatform/ui-common';
import { getListingDeactivation } from '../../../listings/detail/useListingDetailViewQuery';
import { useFeesOverviewRouteParams } from '../_hooks';
import { getRecurringFeeEnumFromFeeTypeRoute } from '../_utils';

export type ListingFeeDetail = NonNullable<
  NonNullable<ReturnType<typeof useListingFeeDrawerQuery>['data']>['listing']
>;

function getFeeSubscriptionStatus({
  today,
  endAt,
  startAt,
}: {
  today: string;
  endAt: Maybe<string>;
  startAt: Maybe<string>;
}): 'current' | 'past' | 'future' {
  if (endAt && (utc(today).isAfter(endAt) || utc(today).isSame(endAt, 'day')))
    return 'past';
  if (startAt && utc(today).isBefore(startAt)) return 'future';
  return 'current';
}

export const getListingFeeSubscriptions = async (
  $api: ApiClient,
  signal: AbortSignal,
  args: {
    listingId: string;
    today: string;
    feeType: recurringFeeType_enum;
  }
) => {
  return await $api
    .GET('/recurring-fees/listing-periods', {
      params: {
        query: {
          listingId: args.listingId,
          recurringFeeType: args.feeType,
        },
      },
      signal,
    })
    .then((result) => {
      if (result.error) throw result.error;
      return result.data.data.map((sub) => {
        const f = {
          ...sub,
          startAt: sub.startAt!,
          endAt: sub.endAt
            ? day(sub.endAt).subtract(1, 'day').yyyymmdd()
            : undefined,
          type: getFeeSubscriptionStatus({
            today: args.today,
            endAt: sub.endAt,
            startAt: sub.startAt,
          }),
        };
        return f as Omit<typeof f, 'listingId'>;
      });
    });
};

export function useListingFeeDrawerQuery(id: Maybe<string>) {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const { feeType } = useFeesOverviewRouteParams();

  const query = useApiQuery(
    async (api, signal, args, db) => {
      if (!args.id)
        return {
          listing: null,
        };

      const [listing, recurringFeeSubscriptions] = await Promise.all([
        db(
          (q) =>
            q
              .listings({
                where: {
                  id: { _eq: args.id },
                },
              })
              .map((listing) => {
                const listingConnections = [
                  {
                    id: listing.connectionId,
                    name: listing.connection?.name,
                    icon: listing.connection?.app.iconRound,
                    app: listing.connection?.app.name,
                  },
                ];

                const activeOwnership = listing
                  .ownershipPeriods({
                    limit: 1,
                    where: whereListingPeriod({
                      date: args.today,
                      type: 'ownership',
                    }),
                  })
                  .map((period) => ({
                    id: period.id,
                    startAt: period.startAt!,
                    endAt: period.endAt,
                    reserve: 0,
                    businessModel: period.businessModel ?? 'managed',
                    members: period.members().map((member) => ({
                      contactId: member.contactId,
                      split: member.split,
                    })),
                  }))[0];

                return {
                  id: listing.id,
                  address: getListingAddress(listing),
                  addressId: listing.address_id,
                  status: listing.status,
                  deactivation: getListingDeactivation(listing, args.today),
                  sourceStatus: listing.source?.status,
                  currency: listing.defaultCurrency,
                  pmsStatus: listing.pmsStatus,
                  name: getListingName(listing),
                  activeOwnership,
                  collection: {
                    id: listing.collectionId,
                    name: listing.collection?.name || '',
                  },
                  pmsName: listing.name,
                  tenantId: teamId,
                  isPmsListing: !!listing.connectionId,
                  listingConnections,
                  // feeSubscriptions,
                };
              })[0],
          {
            signal,
          }
        ),
        getListingFeeSubscriptions(api, signal, {
          listingId: args.id,
          today: args.today,
          feeType: getRecurringFeeEnumFromFeeTypeRoute(args.feeType),
        }),
      ]);

      return {
        listing: {
          ...listing,
          feeSubscriptions: recurringFeeSubscriptions,
        },
      };
    },
    {
      skip: !id,
      queryKeys: ['listings', 'listing-drawer'],
      queryGroup: 'fees',
      uniqueQueryKey: 'listings-fees',
      variables: {
        id,
        teamId,
        GL,
        feeType,
        today: day().yyyymmdd(),
      },
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}
